/* eslint-disable @atlaskit/design-system/no-html-button */
/* eslint-disable jira/react/no-style-attribute, @atlaskit/design-system/ensure-design-token-usage, @atlaskit/ui-styling-standard/enforce-style-prop */
import React, { useEffect, useState } from 'react';
import { useIsHydrateRenderMode } from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
import { useIsGlobalHydrationComplete } from '@jira/ssr__global-hydration-completeness/src/useIsGlobalHydrationComplete.tsx';

// I promise I am being a good developer and not cheating the VC system. This component is strictly for development use
// only and cannot affect VC for customers.
const attrKey = 'vc-nvs';
const IGNORE_VC_ATTRIBUTE = {
	[`data-${attrKey}`]: true,
};

// Added to avoid type errors when checking __HYDRATION_RECOVERABLE_ERRORS__
declare global {
	interface Window {
		__HYDRATION_RECOVERABLE_ERRORS__?: unknown[];
	}
}

export const HydrationAdvisory = (): React.JSX.Element | null => {
	const [isAdvisoryVisible, setIsAdvisoryVisible] = useState(true);
	const [hasRecoverableErrors, setHasRecoverableErrors] = useState(false);
	const [errorsWereDetected, setErrorsWereDetected] = useState(false);
	const [isToastVisible, setIsToastVisible] = useState(false);
	const isHydrateRenderMode = useIsHydrateRenderMode();
	const isGlobalHydrationComplete = useIsGlobalHydrationComplete();

	useEffect(() => {
		const timer = setTimeout(() => {
			setIsAdvisoryVisible(false);
		}, 5000);

		return () => {
			clearTimeout(timer);
		};
	}, []);

	useEffect(() => {
		if (!isGlobalHydrationComplete) {
			return;
		}
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const hasErrors = Boolean(window.__HYDRATION_RECOVERABLE_ERRORS__?.length);
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setHasRecoverableErrors(hasErrors);
		if (hasErrors) {
			setErrorsWereDetected(true);
		}
	}, [isGlobalHydrationComplete]);

	if (!isHydrateRenderMode) {
		return null;
	}

	const dotColor = errorsWereDetected ? 'red' : 'aqua';

	const handleErrorBadgeClick = () => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		console.log('__HYDRATION_RECOVERABLE_ERRORS__: ', window.__HYDRATION_RECOVERABLE_ERRORS__); // oxlint-disable-line no-console
		setIsToastVisible(true);
		setTimeout(() => setIsToastVisible(false), 3000);
	};

	return (
		<div
			{...IGNORE_VC_ATTRIBUTE}
			style={{
				position: 'absolute',
				left: 8,
				top: 12,
				display: 'flex',
				alignItems: 'center',
				// By giving this element a height of 0 and no pointer events, we ensure that it doesn't interfere with the page.
				// To make is visible again, we then set the overflow to "visible".
				height: 0,
				overflow: 'visible',
				pointerEvents: 'none',
				zIndex: 99999999,
			}}
		>
			<div
				style={{
					backgroundColor: dotColor,
					borderRadius: '50%',
					width: 8,
					height: 8,
					zIndex: 20,
				}}
			/>

			{isAdvisoryVisible && (
				<div
					style={{
						backgroundColor: 'midnightblue',
						color: 'aqua',
						height: '1.5em',
						marginLeft: -12,
						paddingLeft: 16,
						paddingRight: 8,
						borderRadius: 4,
						zIndex: 10,
						display: 'flex',
						alignItems: 'center',
						gap: 8,
					}}
				>
					<samp>Rendered with React hydration 🚰 go/jira-hydration</samp>
				</div>
			)}
			{hasRecoverableErrors && (
				<div
					style={{
						backgroundColor: 'darkred',
						color: 'lightyellow',
						height: '1.5em',
						marginLeft: 8,
						paddingLeft: 8,
						paddingRight: 8,
						borderRadius: 4,
						zIndex: 10,
						pointerEvents: 'all',
						display: 'flex',
						alignItems: 'center',
						gap: 8,
					}}
				>
					<button
						type="button"
						onClick={handleErrorBadgeClick}
						style={{
							background: 'none',
							border: 'none',
							color: 'lightyellow',
							cursor: 'pointer',
							font: 'inherit',
							padding: 0,
						}}
					>
						<samp>
							⚠️ Hydration errors detected, click here to reveal in console.
							go/jira-debug-ssr-hydration-failures
						</samp>
					</button>
					<button
						type="button"
						onClick={() => setHasRecoverableErrors(false)}
						style={{
							background: 'none',
							border: 'none',
							color: 'lightyellow',
							cursor: 'pointer',
							padding: 0,
						}}
					>
						<samp>X</samp>
					</button>
				</div>
			)}
			{isToastVisible && (
				<div
					style={{
						backgroundColor: 'darkred',
						color: 'lightyellow',
						height: '1.5em',
						marginLeft: 8,
						paddingLeft: 8,
						paddingRight: 8,
						borderRadius: 4,
						zIndex: 10,
						display: 'flex',
						alignItems: 'center',
					}}
				>
					<samp>Errors logged to console 🔍</samp>
				</div>
			)}
		</div>
	);
};
